// A site encounter imported, and the DM's encounter presets (issue 1047; COMBAT-008, COMBAT-010), the client's half.
// Encounters are built on the site (issue 1048, option B); the server reads them with the DM's own bearer, so every
// call here is to THIS origin (`requestJson`, so `authFetch`). An import is a one-time copy: the server places each
// enemy as a compendium drop is placed, on the free squares near the DM's view this module finds.
import type { Grid, Point } from '@dragons-table/vtt-shared';
import { requestJson } from './api';
import { placeSpot } from './place';

/** The most creatures one import or load places (the server's cap). */
export const IMPORT_MAX = 60;

export interface SiteEncounterRow { id: string; name: string; combatantCount: number }
export interface PresetRow { id: string; name: string; count: number; created_at: string }
export interface PlacingAsk { hidden: boolean; spots: Point[] }

type Refused = { kind: 'refused'; reason: string } | { kind: 'error' };
export type ListAnswer = { kind: 'ok'; encounters: SiteEncounterRow[] } | Refused;
export type PlacedAnswer = { kind: 'ok'; added: number; skipped: number; name: string } | Refused;
export type PresetsAnswer = { kind: 'ok'; presets: PresetRow[] } | Refused;
export type SavedAnswer = { kind: 'ok'; preset: PresetRow; skipped: number } | Refused;

const enc = encodeURIComponent;
const isRow = (v: unknown): v is SiteEncounterRow => {
  const o = v as Record<string, unknown> | null;
  return !!o && typeof o.id === 'string' && typeof o.name === 'string' && typeof o.combatantCount === 'number';
};
const isPreset = (v: unknown): v is PresetRow => {
  const o = v as Record<string, unknown> | null;
  return !!o && typeof o.id === 'string' && typeof o.name === 'string' && typeof o.count === 'number' && typeof o.created_at === 'string';
};
const presetOf = (o: PresetRow): PresetRow => ({ id: o.id, name: o.name, count: o.count, created_at: o.created_at });
/** A refusal in the server's words, else an error. */
const refusedOr = (status: number, body: unknown): Refused => {
  const reason = (body as { reason?: unknown } | null)?.reason;
  if (typeof reason === 'string') return { kind: 'refused', reason };
  if (status === 403) return { kind: 'refused', reason: "That isn't yours to do." };
  return { kind: 'error' };
};
const placedOf = (status: number, body: unknown): PlacedAnswer => {
  const b = (body ?? {}) as { added?: unknown; skipped?: unknown; name?: unknown };
  if ((status === 200 || status === 201) && typeof b.added === 'number' && typeof b.name === 'string') {
    return { kind: 'ok', added: b.added, skipped: typeof b.skipped === 'number' ? b.skipped : 0, name: b.name };
  }
  return refusedOr(status, body);
};
const placingBody = (ask: PlacingAsk, sceneId: string | null) => JSON.stringify({
  hidden: ask.hidden, spots: ask.spots.slice(0, IMPORT_MAX).map((p) => ({ x: Math.round(p.x), y: Math.round(p.y) })), ...(sceneId ? { scene_id: sceneId } : {}),
});

/** The DM's encounters on the site, in the table's game system. */
export async function siteEncounterList(campaignId: string, fetchImpl: typeof fetch = fetch): Promise<ListAnswer> {
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/site-encounters`, {}, fetchImpl);
  const list = (body as { encounters?: unknown } | null)?.encounters;
  if (status === 200 && Array.isArray(list)) return { kind: 'ok', encounters: list.filter(isRow).map((r) => ({ id: r.id, name: r.name, combatantCount: r.combatantCount })) };
  return refusedOr(status, body);
}

/** Import one (a copy): its enemies placed on the scene in view, hidden or not. */
export async function importSiteEncounter(campaignId: string, id: string, ask: PlacingAsk, sceneId: string | null, fetchImpl: typeof fetch = fetch): Promise<PlacedAnswer> {
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/site-encounters/${enc(id)}/import`, { method: 'POST', body: placingBody(ask, sceneId) }, fetchImpl);
  return placedOf(status, body);
}

export async function presetList(campaignId: string, fetchImpl: typeof fetch = fetch): Promise<PresetsAnswer> {
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/encounter-presets`, {}, fetchImpl);
  const list = (body as { presets?: unknown } | null)?.presets;
  if (status === 200 && Array.isArray(list)) return { kind: 'ok', presets: list.filter(isPreset).map(presetOf) };
  return refusedOr(status, body);
}

/** Save the turn order of the scene in view as a preset. */
export async function savePreset(campaignId: string, name: string, sceneId: string | null, fetchImpl: typeof fetch = fetch): Promise<SavedAnswer> {
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/encounter-presets`, { method: 'POST', body: JSON.stringify({ name, ...(sceneId ? { scene_id: sceneId } : {}) }) }, fetchImpl);
  if (status === 201 && isPreset(body)) {
    const skipped = (body as unknown as { skipped?: unknown }).skipped;
    return { kind: 'ok', preset: presetOf(body), skipped: typeof skipped === 'number' ? skipped : 0 };
  }
  if (status === 400) return { kind: 'refused', reason: 'A preset needs a name of 1 to 80 characters.' };
  return refusedOr(status, body);
}

export async function loadPreset(campaignId: string, id: string, ask: PlacingAsk, sceneId: string | null, fetchImpl: typeof fetch = fetch): Promise<PlacedAnswer> {
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/encounter-presets/${enc(id)}/load`, { method: 'POST', body: placingBody(ask, sceneId) }, fetchImpl);
  return placedOf(status, body);
}

export async function deletePreset(campaignId: string, id: string, fetchImpl: typeof fetch = fetch): Promise<boolean> {
  const { status } = await requestJson(`/api/tables/${enc(campaignId)}/encounter-presets/${enc(id)}`, { method: 'DELETE' }, fetchImpl);
  return status === 204;
}

/** `n` free squares near `point`, ring by ring outwards: each the first square nothing (nor an earlier one) stands on. */
export function freeSpots(grid: Grid, extent: { width: number; height: number }, point: Point, snap: boolean, taken: Point[], n: number): Point[] {
  const busy = [...taken];
  const out: Point[] = [];
  for (let i = 0; i < n; i += 1) {
    const p = placeSpot(grid, extent, point, snap, busy, true);
    // Unsnapped, or the rings are full: the server lays the rest in a block from the first.
    if (busy.some((t) => Math.abs(t.x - p.x) < 1 && Math.abs(t.y - p.y) < 1)) break;
    out.push(p);
    busy.push(p);
  }
  return out;
}

/** "Added 4 from 'Goblin ambush'; 1 player skipped." */
export function importNotice(name: string, added: number, skipped: number): string {
  const skip = skipped > 0 ? `; ${skipped} player${skipped === 1 ? '' : 's'} skipped` : '';
  return `Added ${added} from '${name}'${skip}.`;
}
